import _ from "lodash";
import React from "react";
import i18n from "src/i18n/config";

import { AndroidModule, Product, Sources } from "@dashboard/products/types";

const partitionOptions = [
    { value: "odm", label: "/odm" },
    { value: "product", label: "/product" },
    { value: "system", label: "/system" },
    { value: "system_ext", label: "/system_ext" },
    { value: "vendor", label: "/vendor" },
];

const addableModuleTemplates: AndroidModule[] = [
    {
        name: "cta_predefined_system_include",
        type: "existing_module",
        tags: ["Build"],
    },
    {
        name: "cta_predefined_system_exclude",
        type: "remove_override",
        tags: ["Build"],
    },
    {
        name: "cta_predefined_own_application",
        type: "prebuilt_app",
        tags: ["Own"],
        sourceId: "",
    },
    {
        name: "cta_predefined_own_executable",
        type: "prebuilt_executable",
        tags: ["Own"],
        sourceId: "",
    },
    {
        name: "com.emteria.update",
        type: "existing_module",
        tags: ["System"],
    },
    {
        name: "com.emteria.kiosk",
        type: "existing_module",
        tags: ["System"],
    },
    {
        name: "com.emteria.storage",
        type: "existing_module",
        tags: ["System"],
    },
    {
        name: "com.emteria.mdm",
        type: "existing_module",
        tags: ["System"],
    },
    {
        name: "com.emteria.headless",
        type: "existing_module",
        tags: ["System"],
    },
    {
        name: "cta_prebuilt_bootanimation",
        type: "prebuilt_bootanimation",
        tags: ["Own"],
        sourceId: "",
    },
    {
        name: "cta_predefined_downloadable_file",
        type: "downloadable_file",
        tags: ["Build"],
        sourceId: "",
    },
];

function checkFileSourceExists(modules: AndroidModule[], sources: Sources, fileId: string) {
    const sourceId = Object.keys(sources).find((item) => sources[item].fileId === fileId);
    if (!sourceId) {
        return false;
    }

    return modules.some((item) => item.sourceId === sourceId);
}
function getTags(modules: AndroidModule[]) {
    const tags = ["Added", "All", "Own", "System"];
    modules.forEach((module) => {
        tags.push(...module.tags);
    });
    const uniqueTags = _.uniq(tags);
    return _.orderBy(uniqueTags, (tag) => tag);
}
function filterModules<T extends AndroidModule>(modules: T[], tag: string): T[] {
    return modules.filter((module) => {
        return tag === "All" || module.tags.includes(tag);
    });
}
function excludeAlreadyAddedModules(templateModules: AndroidModule[], addedModules: AndroidModule[]): AndroidModule[] {
    return templateModules.filter((template) => {
        if (template.type === "prebuilt_bootanimation") {
            return !addedModules.some((added) => added.type === "prebuilt_bootanimation");
        }
        return !addedModules.some((added) => added.name === template.name);
    });
}
function getProductHeaderTitle(product: Product) {
    const productName = product.customBuildConfig?.productName;
    const version = product.androidVersion;

    if (productName && version) {
        return productName + " - Android " + version;
    }

    if (productName) {
        return productName;
    }

    return i18n.t("sidepanels:modulePanel.unknownProduct");
}

type SearchContextType = {
    search: string;
    onChange: (search: string) => void;
};

const SearchContext = React.createContext<SearchContextType>({
    search: "",
    onChange: () => {},
});

type SearchProviderProps = {
    children: (ctx: SearchContextType) => React.ReactNode;
};
const SearchProvider = (props: SearchProviderProps) => {
    const [search, setSearch] = React.useState("");

    const onChange = _.debounce((value: string) => setSearch(value.toLowerCase()), 200);

    return <SearchContext.Provider value={{ search, onChange }}>{props.children({ search, onChange })}</SearchContext.Provider>;
};

const useSearch = () => {
    const context = React.useContext(SearchContext);
    if (!context) {
        throw new Error("useSearch must be used within a SearchProvider");
    }
    return context;
};

export default {
    addableModuleTemplates,
    checkFileSourceExists,
    excludeAlreadyAddedModules,
    filterModules,
    getProductHeaderTitle,
    getTags,
    partitionOptions,
    SearchProvider,
    useSearch,
};
